import React from 'react';
import { X, Copy, Lock } from 'lucide-react';

/**
 * ClientDetailPanel — Slide-out panel showing client details.
 * Supports AI suspend/resume actions.
 */
export default function ClientDetailPanel({ client, onClose, onSuspendAI, onResumeAI, onOverride }) {
    const Badge = ({ color, children }) => (
        <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, fontSize: '0.82rem', color: '#1a1b2e', fontWeight: 500 }}>
            <span style={{ width: 8, height: 8, borderRadius: '50%', background: color, display: 'inline-block', flexShrink: 0 }} />
            {children}
        </span>
    );
    const getRemainingDays = () => {
        if (!client || !client.subscription_expires_at) return 30;
        const diff = new Date(client.subscription_expires_at) - new Date();
        const days = Math.ceil(diff / (1000 * 60 * 60 * 24));
        return days > 0 ? days : 30;
    };

    const [overridePlan, setOverridePlan] = React.useState(client?.plan || 'starter');
    const [overrideDays, setOverrideDays] = React.useState(getRemainingDays());

    if (!client) return null;

    const isConnected = !!client.whatsapp_number || client.whatsapp_connected;
    const isSuspended = client.ai_suspended;

    const getDynamicExpiry = () => {
        if (client.subscription_expires_at) return new Date(client.subscription_expires_at);
        if (client.created_at) {
            const date = new Date(client.created_at);
            date.setDate(date.getDate() + 7);
            return date;
        }
        return null;
    };

    const expiryDate = getDynamicExpiry();

    return (
        <>
            <div className="detail-panel-overlay" onClick={onClose} />
            <div className="detail-panel">
                <div className="detail-panel__header">
                    <h2>{client.name || client.username}</h2>
                    <button className="detail-panel__close" onClick={onClose}>
                        <X size={18} />
                    </button>
                </div>

                <div className="detail-panel__body">
                    {/* ── Contact Info Section ── */}
                    <div style={{ background: 'var(--bg-light,#f8fafc)', borderRadius: 8, padding: '12px 14px', marginBottom: 8 }}>
                        <div style={{ fontSize: '0.72rem', fontWeight: 700, letterSpacing: '0.08em', color: '#94a3b8', marginBottom: 10, textTransform: 'uppercase' }}>Contact Info</div>
                        {[
                            { label: 'Username', value: client.username },
                            { label: 'Email', value: client.email },
                            { label: 'Phone', value: client.owner_phone_number },
                        ].map(({ label, value }) => (
                            <div key={label} className="detail-row" style={{ alignItems: 'center' }}>
                                <span className="detail-row__label">{label}</span>
                                <span className="detail-row__value" style={{ display: 'flex', alignItems: 'center', gap: 6 }}>
                                    {value
                                        ? <>
                                            <span style={{ fontFamily: 'monospace', fontSize: '0.82rem' }}>{value}</span>
                                            <button
                                                title="Copy"
                                                onClick={() => navigator.clipboard.writeText(value)}
                                                style={{ background: 'none', border: 'none', cursor: 'pointer', padding: '2px 4px', borderRadius: 4, color: '#94a3b8', display: 'flex', alignItems: 'center' }}
                                            ><Copy size={12} /></button>
                                          </>
                                        : <span style={{ color: '#94a3b8' }}>—</span>}
                                </span>
                            </div>
                        ))}
                        <div className="detail-row">
                            <span className="detail-row__label">Password</span>
                            <span className="detail-row__value" style={{ color: '#94a3b8', fontSize: '0.78rem', display: 'flex', alignItems: 'center', gap: 5 }}>
                                <Lock size={11} /> Hashed — cannot be displayed
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-row__label">Joined</span>
                            <span className="detail-row__value">
                                {client.created_at
                                    ? new Date(client.created_at).toLocaleString('en-IN', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })
                                    : '—'}
                            </span>
                        </div>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Status</span>
                        <Badge color={(client.status || '').toLowerCase() === 'active' ? '#22c55e' : (client.status || '').toLowerCase() === 'pending connection' ? '#f59e0b' : '#94a3b8'}>
                            {client.status || 'Unknown'}
                        </Badge>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">WhatsApp</span>
                        <Badge color={isConnected ? '#22c55e' : '#94a3b8'}>
                            {isConnected ? client.whatsapp_number || 'Connected' : 'Not Connected'}
                        </Badge>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Messages Sent</span>
                        <span className="detail-row__value">{client.messages_sent ?? '—'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Messages Received</span>
                        <span className="detail-row__value">{client.messages_received ?? '—'}</span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Last Active</span>
                        <span className="detail-row__value">
                            {client.last_active
                                ? new Date(client.last_active).toLocaleDateString('en-US', {
                                    month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit'
                                })
                                : '—'}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">AI Status</span>
                        <Badge color={isSuspended ? '#ef4444' : '#22c55e'}>
                            {isSuspended ? 'Suspended' : 'Active'}
                        </Badge>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Subscription</span>
                        <Badge color={client.subscription_status === 'active' ? '#22c55e' : client.subscription_status === 'pending' ? '#f59e0b' : '#94a3b8'}>
                            {client.subscription_status || 'trial'}
                        </Badge>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Plan</span>
                        <span className="detail-row__value" style={{ textTransform: 'capitalize' }}>
                            {client.subscription_status === 'trial'
                                ? 'Free Trial'
                                : client.plan === 'starter' ? 'Starter'
                                : client.plan === 'business' ? 'Business'
                                : client.plan === 'enterprise' ? 'Enterprise'
                                : client.plan || 'Starter'}
                        </span>
                    </div>
                    <div className="detail-row">
                        <span className="detail-row__label">Expires</span>
                        <span className="detail-row__value" style={{
                            color: expiryDate
                                ? (expiryDate < new Date() ? '#ef4444' : '#1a1b2e')
                                : '#94a3b8'
                        }}>
                            {expiryDate
                                ? expiryDate.toLocaleString('en-IN', {
                                    day: 'numeric', month: 'short', year: 'numeric',
                                    hour: '2-digit', minute: '2-digit'
                                })
                                : 'Not set'}
                        </span>
                    </div>

                    <div style={{ marginTop: 24, borderTop: '1px solid var(--border)', paddingTop: 16 }}>
                        <h3 style={{ fontSize: '0.88rem', margin: '0 0 12px 0' }}>Subscription Override</h3>
                    <div className="detail-row" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                        <select 
                            value={overridePlan} 
                            onChange={e => setOverridePlan(e.target.value)}
                            style={{ padding: '6px', borderRadius: '4px', border: '1px solid var(--border)' }}
                        >
                            <option value="starter">Starter</option>
                            <option value="business">Business</option>
                            <option value="enterprise">Enterprise</option>
                        </select>
                        <input 
                            type="number" 
                            min="1" 
                            value={overrideDays} 
                            onChange={e => setOverrideDays(e.target.value)}
                            style={{ width: '60px', padding: '6px', borderRadius: '4px', border: '1px solid var(--border)' }}
                        />
                        <span style={{ fontSize: '0.78rem' }}>days</span>
                        <button 
                            className="admin-btn primary" 
                            style={{ padding: '4px 8px', marginLeft: 'auto' }}
                            onClick={() => onOverride(client.id, overridePlan, overrideDays)}
                        >
                            Apply
                        </button>
                    </div>
                </div>
                </div>

                <div className="detail-panel__actions">
                    {isSuspended ? (
                        <button className="admin-btn primary" onClick={() => onResumeAI(client.id)}>
                            Resume AI
                        </button>
                    ) : (
                        <button className="admin-btn danger" onClick={() => onSuspendAI(client.id)}>
                            Suspend AI
                        </button>
                    )}
                </div>
            </div>
        </>
    );
}
